import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import './UpcomingMedicine.css';
import ArrowLeft from '../../../../assets/icons/arrowgauche.png';
import ArrowRight from '../../../../assets/icons/arrowdroit.png';
import HeroImage from '../../../../assets/images/med.jpg';
import { fetchMedications } from '../../../../medications/redux/thunks/medicationThunks';
import { selectActiveMedications, selectMedicationState } from '../../../../medications/redux/slices/medicationSlice';

const UpcomingMedicine = ({ userId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const meds = useSelector(selectActiveMedications);
  const { loading } = useSelector(selectMedicationState);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [tick, setTick] = useState(0);

  // Fetch medications when userId is available
  useEffect(() => {
    if (userId) {
      dispatch(fetchMedications({ userId }));
    }
  }, [dispatch, userId]);

  // Sort meds by date
  const sortedMeds = useMemo(() => {
    if (!Array.isArray(meds) || meds.length === 0) return [];
    return [...meds].sort((a, b) => {
      const ta = new Date(a.createdAt || a.updatedAt || 0).getTime();
      const tb = new Date(b.createdAt || b.updatedAt || 0).getTime();
      return tb - ta;
    });
  }, [meds]);

  // Find next dose
  const getNextDoseTime = (med) => {
    if (!med?.times?.length) return null;

    const now = new Date();
    const takenArray = Array.isArray(med.takenPerDose) ? med.takenPerDose : [];

    for (let i = 0; i < med.times.length; i++) {
      if (!takenArray[i]) {
        const doseTime = new Date(med.times[i]);
        if (doseTime > now) return doseTime;
      }
    }

    const firstDose = new Date(med.times[0]);
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(firstDose.getHours(), firstDose.getMinutes(), 0, 0);
    return tomorrow;
  };

  const currentMedicine = sortedMeds[currentIndex];
  const nextDoseTime = currentMedicine ? getNextDoseTime(currentMedicine) : null;

  // Timer
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const { hh, mm, ss } = useMemo(() => {
    if (!nextDoseTime) return { hh: '0', mm: '00', ss: '00' };
    const timeLeft = Math.max(0, nextDoseTime.getTime() - Date.now());
    const total = Math.floor(timeLeft / 1000);
    return {
      hh: String(Math.floor(total / 3600)).padStart(1, '0'),
      mm: String(Math.floor((total % 3600) / 60)).padStart(2, '0'),
      ss: String(total % 60).padStart(2, '0'),
    };
  }, [nextDoseTime, tick]);

  const goToPrevious = () => setCurrentIndex(prev => (prev - 1 + sortedMeds.length) % sortedMeds.length);
  const goToNext = () => setCurrentIndex(prev => (prev + 1) % sortedMeds.length);

  const getFrequencyText = (med) => {
    if (!med) return '';
    return t('upcomingMedicine.frequency', {
      count: med.amount,
      dose: med.amount > 1 ? t('upcomingMedicine.doses') : t('upcomingMedicine.dose')
    });
  };

  // Formater les horaires
  const getTimesText = (med) => {
    if (!med || !Array.isArray(med.times)) return '';
    return med.times.map(t => {
      const d = new Date(t);
      return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Tunis' });
    }).join(', ');
  };

  // Résoudre la photo : image uploadée par l'user en priorité, sinon default
  const getMedPhoto = (med) => {
    if (!med) return HeroImage;
    // Couvre tous les noms possibles retournés par le backend
    const url = med.imageUrl || med.image_url || med.image || med.photo || med.photoUrl;
    // Ignorer si c'est un File/Blob objet (pas une string URL)
    if (url && typeof url === 'string' && url.trim() !== '') return url;
    return HeroImage;
  };

  // Si chargement
  if (loading && sortedMeds.length === 0) {
    return (
      <section className="um-card">
        <h4 className="um-title">{t('upcomingMedicine.title')}</h4>
        <div className="um-info" style={{ textAlign: 'center', padding: '2rem' }}>
          {t('upcomingMedicine.loading')}
        </div>
      </section>
    );
  }

  // Si aucun médicament
  if (sortedMeds.length === 0) {
    return (
      <section className="um-card">
        <h4 className="um-title">{t('upcomingMedicine.title')}</h4>
        <div className="um-info" style={{ textAlign: 'center', padding: '2rem' }}>
          {t('upcomingMedicine.noMedications')}
        </div>
      </section>
    );
  }

  return (
    <section className="um-card">
      <h4 className="um-title">{t('upcomingMedicine.title')}</h4>

      <div className="um-hero">
        <button
          className="um-nav"
          aria-label={t('upcomingMedicine.previous')}
          onClick={goToPrevious}
          disabled={sortedMeds.length <= 1}
        >
          <img src={ArrowLeft} alt="" width={12} height={12} />
        </button>

        <img
          className="um-hero-img"
          src={getMedPhoto(currentMedicine)}
          alt={t('upcomingMedicine.medicineAlt')}
          onError={(e) => { e.currentTarget.src = HeroImage; }}
        />

        <button
          className="um-nav"
          aria-label={t('upcomingMedicine.next')}
          onClick={goToNext}
          disabled={sortedMeds.length <= 1}
        >
          <img src={ArrowRight} alt="" width={12} height={12} />
        </button>
      </div>

      <div className="um-info">
        <div className="um-line">
          <span className="um-label">{t('upcomingMedicine.name')} :</span>
          <span className="um-name">{currentMedicine?.name || t('upcomingMedicine.na')}</span>
        </div>

        <div className="um-line um-detail">
          <span className="um-label">{t('upcomingMedicine.dosage')} :</span>
          <span className="um-detail-text">{currentMedicine?.dosage || t('upcomingMedicine.na')}</span>
        </div>

        <div className="um-line um-detail">
          <span className="um-label">{t('upcomingMedicine.times')} :</span>
          <span className="um-detail-text">{getTimesText(currentMedicine)}</span>
        </div>

        <div className="um-detail-text center">{getFrequencyText(currentMedicine)}</div>

        <div className="um-timer" aria-label={t('upcomingMedicine.timeRemaining')}>
          {hh}:{mm}:{ss}
        </div>

        <button className="um-link" type="button">
          {t('upcomingMedicine.viewAllDetails')}
        </button>

        {sortedMeds.length > 1 && (
          <div className="um-pagination">
            {currentIndex + 1} / {sortedMeds.length}
          </div>
        )}
      </div>
    </section>
  );
};

export default UpcomingMedicine;